import React from 'react';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  MARKDOWN_HEADING_SIZE,
  MARKDOWN_HEADING_SIZE_FALLBACK,
  MARKDOWN_PATTERNS,
  ORDERED_ITEM,
  UNORDERED_ITEM,
} from '@constants/markdown';
import { expandLiteralValues } from '@/utils/literalValues';

interface MarkdownRendererProps {
  content: string;
  variant?: 'body1' | 'body2';
  className?: string;
  /** Source text the content holds as placeholders; always rendered as text. */
  literalValues?: string[];
}

const CODE_BLOCK =
  'my-2 overflow-auto whitespace-pre-wrap break-words rounded-lg border bg-muted p-3 font-mono text-sm';
const INLINE_CODE = 'rounded bg-muted px-1 py-0.5 font-mono text-[0.875em] text-foreground';
const BLOCKQUOTE = 'my-2 border-l-4 border-primary pl-4 italic text-muted-foreground';
// Markers are set explicitly: the .revy-ui preflight sets list-style:none, which
// is why ordered lists rendered without numbers (issue #781).
const LIST = 'my-1 pl-6';

export const isMarkdownContent = (content: string): boolean =>
  MARKDOWN_PATTERNS.some((pattern) => pattern.test(content));

/**
 * Bold, italic and inline code, applied recursively to bold segments.
 * Links are never rendered (#1361): a quoted URL stays text.
 */
function formatInline(text: string, lit: (text: string) => string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{formatInline(boldMatch[1], lit)}</span>);
      parts.push(<strong key={key++}>{formatInline(boldMatch[2], lit)}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    const italicMatch = remaining.match(/^(.*?)\*(.+?)\*(.*)/s);
    if (italicMatch) {
      if (italicMatch[1]) parts.push(<span key={key++}>{lit(italicMatch[1])}</span>);
      parts.push(<em key={key++}>{lit(italicMatch[2])}</em>);
      remaining = italicMatch[3];
      continue;
    }

    const codeMatch = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{lit(codeMatch[1])}</span>);
      parts.push(
        <code key={key++} className={INLINE_CODE}>
          {lit(codeMatch[2])}
        </code>,
      );
      remaining = codeMatch[3];
      continue;
    }

    parts.push(<span key={key++}>{lit(remaining)}</span>);
    break;
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

/** Collects consecutive list items, tolerating a blank line between them. */
function collectListItems(lines: string[], start: number, marker: RegExp) {
  const items: string[] = [];
  let i = start;

  while (i < lines.length) {
    const current = lines[i].trim();
    if (marker.test(current)) {
      items.push(current.replace(marker, ''));
      i++;
    } else if (current === '' && i + 1 < lines.length && marker.test(lines[i + 1].trim())) {
      i++;
    } else {
      break;
    }
  }

  return { items, next: i };
}

/**
 * Built-in Markdown renderer (no external dependency). Handles headings, lists,
 * bold, italic, code blocks, inline code, blockquotes and rules.
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  variant = 'body2',
  className,
  literalValues,
}) => {
  const lit = (text: string) => expandLiteralValues(text, literalValues);
  const textSize = variant === 'body1' ? 'text-base' : 'text-sm';
  const elements: React.ReactNode[] = [];
  const lines = content.split('\n');
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (trimmed.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={key++} className={CODE_BLOCK}>
          <code>{lit(codeLines.join('\n'))}</code>
        </pre>,
      );
      i++;
      continue;
    }

    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
      elements.push(<Separator key={key++} className="my-4" />);
      i++;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      elements.push(
        <h6
          key={key++}
          className={cn(
            MARKDOWN_HEADING_SIZE[level] ?? MARKDOWN_HEADING_SIZE_FALLBACK,
            // After the size class: tailwind-merge treats a text-* size as
            // conflicting with leading-*, so it drops a leading set before it.
            'revy-cjk mb-1 mt-4 font-semibold leading-[1.5]',
          )}
        >
          {formatInline(headingMatch[2], lit)}
        </h6>,
      );
      i++;
      continue;
    }

    if (trimmed.startsWith('> ')) {
      const quoteLines: string[] = [trimmed.slice(2)];
      i++;
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(
        <blockquote key={key++} className={BLOCKQUOTE}>
          <p className={cn(textSize, 'revy-cjk leading-[1.5]')}>
            {formatInline(quoteLines.join(' '), lit)}
          </p>
        </blockquote>,
      );
      continue;
    }

    const isUnordered = UNORDERED_ITEM.test(trimmed);
    if (isUnordered || ORDERED_ITEM.test(trimmed)) {
      const marker = isUnordered ? UNORDERED_ITEM : ORDERED_ITEM;
      const { items, next } = collectListItems(lines, i, marker);
      const ListTag = isUnordered ? 'ul' : 'ol';
      elements.push(
        <ListTag key={key++} className={cn(LIST, isUnordered ? 'list-disc' : 'list-decimal')}>
          {items.map((item, index) => (
            <li key={index} className={cn(textSize, 'revy-cjk leading-[1.5]')}>
              {formatInline(item, lit)}
            </li>
          ))}
        </ListTag>,
      );
      i = next;
      continue;
    }

    if (trimmed === '') {
      i++;
      continue;
    }

    elements.push(
      <p key={key++} className={cn(textSize, 'revy-cjk mb-2 leading-[1.5]')}>
        {formatInline(trimmed, lit)}
      </p>,
    );
    i++;
  }

  return <div className={className}>{elements}</div>;
};

export default MarkdownRenderer;
